import React, { useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  Activity,
  ArrowUpRight,
  Banknote,
  Briefcase,
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  Clock,
  Database,
  FileText,
  Flame,
  FolderGit2,
  Layers3,
  LayoutDashboard,
  LogOut,
  Mail,
  MessageSquare,
  Phone,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Share2,
  Sliders,
  Target,
  Trash2,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { SocialBoard } from './SocialBoard';

const Presupuestos = React.lazy(() =>
  import('./Presupuestos').then((m) => ({ default: m.Presupuestos }))
);
import {
  CashLedgerEntry,
  Company,
  Contact,
  Interaction,
  LeadCanal,
  LeadStage,
  Project,
  ProjectStatus,
  Proposal,
  User,
  WeeklyMetric,
} from '../types';
import { isSupabaseConfigured, supabase } from '../lib/supabase';

interface VisualReadOnlyHubProps {
  user: User;
  workspaceId: string;
  onLogout: () => void;
}

export type MainSection = 'GESTION' | 'CLIENTES' | 'VENTAS' | 'REDES';
export type GestionSubView = 'PROJECTS' | 'METRICS' | 'CASH';
export type VentasSubView = 'PRESUPUESTOS';
export type ClientesSubView = 'PIPELINE' | 'CLIENTS';

interface HubData {
  companies: Company[];
  contacts: Contact[];
  interactions: Interaction[];
  projects: Project[];
  proposals: Proposal[];
  weeklyMetrics: WeeklyMetric[];
  cashLedger: CashLedgerEntry[];
}

const EMPTY_DATA: HubData = {
  companies: [],
  contacts: [],
  interactions: [],
  projects: [],
  proposals: [],
  weeklyMetrics: [],
  cashLedger: [],
};

const STAGE_ORDER: LeadStage[] = [
  LeadStage.NEW,
  LeadStage.EMAIL_DRAFT,
  LeadStage.CONTACTED,
  LeadStage.MEETING,
  LeadStage.PROPOSAL,
  LeadStage.CLOSED_WON,
  LeadStage.CLOSED_LOST,
];

const STAGE_LABELS: Record<string, string> = {
  NEW: 'Nuevo',
  EMAIL_DRAFT: 'Email listo',
  CONTACTED: 'Contactado',
  MEETING: 'Reunión',
  PROPOSAL: 'Propuesta',
  CLOSED_WON: 'Ganado',
  CLOSED_LOST: 'Perdido',
  INTERESTED: 'Interés',
  NEGOTIATION: 'Negociación',
};

const CANAL_LABELS: Record<LeadCanal | 'UNKNOWN', string> = {
  COLD_EMAIL: 'Cold email',
  LINKEDIN: 'LinkedIn',
  PHONE: 'Teléfono',
  UNKNOWN: 'Sin canal',
};

const currency = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

const compactNumber = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 1,
});

const toNumber = (value: unknown, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const formatDate = (date?: string) => {
  if (!date) return 'Sin fecha';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return date;
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short' }).format(parsed);
};

const daysSince = (date?: string) => {
  if (!date) return null;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - parsed.getTime()) / 86_400_000));
};

const latestDate = (dates: (string | undefined)[]) => {
  const valid = dates
    .map((date) => (date ? new Date(date) : null))
    .filter((date): date is Date => !!date && !Number.isNaN(date.getTime()))
    .sort((a, b) => b.getTime() - a.getTime());
  return valid[0]?.toISOString();
};

const mapCompany = (row: any): Company => ({
  id: row.id,
  workspaceId: row.workspace_id,
  name: row.name ?? 'Sin nombre',
  sector: row.sector ?? 'Sin sector',
  stage: row.stage as LeadStage,
  score: toNumber(row.score),
  nextContactAt: row.next_contact_at ?? undefined,
  createdAt: row.created_at ?? '',
  followUpAttempts: row.follow_up_attempts ?? 0,
  ownerId: row.owner_id ?? '',
  website: row.website ?? undefined,
  region: row.region ?? undefined,
  employeeCount: row.employee_count != null ? String(row.employee_count) : undefined,
  notes: row.notes ?? undefined,
  canal: row.canal ?? undefined,
  linkedinUrl: row.linkedin_url ?? undefined,
  source: row.source ?? undefined,
});

const mapContact = (row: any): Contact => ({
  id: row.id,
  companyId: row.company_id,
  name: row.name ?? 'Contacto',
  email: row.email ?? '',
  role: row.role ?? undefined,
  isPrimary: row.is_primary ?? false,
  linkedinUrl: row.linkedin_url ?? undefined,
});

const mapInteraction = (row: any): Interaction => ({
  id: row.id,
  companyId: row.company_id,
  date: row.date,
  type: row.type ?? 'EMAIL',
  notes: row.notes ?? '',
  nextSteps: row.next_steps ?? undefined,
});

const mapProject = (row: any): Project => ({
  id: row.id,
  workspaceId: row.workspace_id ?? '',
  clientCompanyId: row.company_id ?? row.client_company_id,
  name: row.name ?? 'Proyecto',
  status: row.status as ProjectStatus,
  progress: toNumber(row.progress),
  description: row.description ?? undefined,
  startDate: row.start_date ?? row.created_at ?? '',
  endDate: row.end_date ?? undefined,
});

const mapProposal = (row: any): Proposal => ({
  id: row.id,
  companyId: row.company_id,
  date: row.date ?? row.created_at ?? '',
  total: toNumber(row.total),
  status: row.status ?? 'DRAFT',
  items: Array.isArray(row.items) ? row.items : [],
});

const mapWeeklyMetric = (row: any): WeeklyMetric => ({
  id: row.id,
  weekStart: row.week_start,
  empresasContactadas: toNumber(row.empresas_contactadas),
  conversaciones: toNumber(row.conversaciones),
  posts: toNumber(row.posts),
  entrenoDias: toNumber(row.entreno_dias),
  movilHoras: toNumber(row.movil_horas),
  autovaloracion: toNumber(row.autovaloracion),
  diagnosticos: toNumber(row.diagnosticos),
  retainers: toNumber(row.retainers),
  notas: row.notas ?? undefined,
});

const mapCashLedgerEntry = (row: any): CashLedgerEntry => ({
  id: row.id,
  date: row.date,
  concepto: row.concepto ?? 'Movimiento',
  companyId: row.company_id ?? undefined,
  importe: toNumber(row.importe),
  tipo: row.tipo ?? 'PREVISTO',
  cobrado: Boolean(row.cobrado),
  notas: row.notas ?? undefined,
});

async function selectRows(table: string, query: any): Promise<any[]> {
  const { data, error } = await query;
  if (error) {
    console.info(`SDAI read-only hub: ${table} returned no readable data`, error.message);
    return [];
  }
  return data ?? [];
}

export const VisualReadOnlyHub: React.FC<VisualReadOnlyHubProps> = ({ user, workspaceId, onLogout }) => {
  // Navegación en 3 pestañas principales: Gestión, Clientes, Redes
  const [activeSection, setActiveSection] = useState<MainSection>('GESTION');
  const [gestionSubView, setGestionSubView] = useState<GestionSubView>('PROJECTS');
  const [ventasSubView, setVentasSubView] = useState<VentasSubView>('PRESUPUESTOS');
  const [clientesSubView, setClientesSubView] = useState<ClientesSubView>('PIPELINE');

  const [data, setData] = useState<HubData>(EMPTY_DATA);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [canalFilter, setCanalFilter] = useState<'ALL' | LeadCanal | 'UNKNOWN'>('ALL');
  const [sectorFilter, setSectorFilter] = useState('ALL');
  const [reloadToken, setReloadToken] = useState(0);

  const loadData = async () => {
    if (!supabase) {
      setData(EMPTY_DATA);
      setLoading(false);
      return;
    }

    setLoading(true);
    const companyRows = await selectRows(
      'companies',
      supabase
        .from('companies')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('score', { ascending: false })
    );
    const companies = companyRows.map(mapCompany);
    const companyIds = companies.map((company) => company.id);

    const [contactRows, interactionRows, projectRows, proposalRows, weeklyRows, cashRows] = await Promise.all([
      companyIds.length ? selectRows('contacts', supabase.from('contacts').select('*').in('company_id', companyIds)) : [],
      companyIds.length
        ? selectRows('interactions', supabase.from('interactions').select('*').in('company_id', companyIds).order('date', { ascending: false }))
        : [],
      // Proyectos por workspace (no por company_id): si no, los "Sin cliente" desaparecen tras crearlos.
      selectRows('projects', supabase.from('projects').select('*').eq('workspace_id', workspaceId).order('start_date', { ascending: false })),
      companyIds.length
        ? selectRows('proposals', supabase.from('proposals').select('*').in('company_id', companyIds).order('date', { ascending: false }))
        : [],
      selectRows('weekly_metrics', supabase.from('weekly_metrics').select('*').order('week_start', { ascending: true })),
      selectRows('cash_ledger', supabase.from('cash_ledger').select('*').order('date', { ascending: false })),
    ]);

    setData({
      companies,
      contacts: contactRows.map(mapContact),
      interactions: interactionRows.map(mapInteraction),
      projects: projectRows.map(mapProject),
      proposals: proposalRows.map(mapProposal),
      weeklyMetrics: weeklyRows.map(mapWeeklyMetric),
      cashLedger: cashRows.map(mapCashLedgerEntry),
    });

    setLastUpdated(new Date().toISOString());
    setLoading(false);
  };

  useEffect(() => {
    void loadData();
  }, [workspaceId]);

  const companyById = useMemo(() => new Map(data.companies.map((company) => [company.id, company])), [data.companies]);

  const sectors = useMemo(
    () => ['ALL', ...Array.from(new Set(data.companies.map((company) => company.sector).filter(Boolean))).sort()],
    [data.companies]
  );

  const filteredCompanies = useMemo(
    () =>
      data.companies.filter((company) => {
        const matchesCanal =
          canalFilter === 'ALL' ||
          (canalFilter === 'UNKNOWN' && !company.canal) ||
          company.canal === canalFilter;
        const matchesSector = sectorFilter === 'ALL' || company.sector === sectorFilter;
        return matchesCanal && matchesSector;
      }),
    [canalFilter, data.companies, sectorFilter]
  );

  const funnel = useMemo(
    () =>
      STAGE_ORDER.map((stage) => ({
        stage,
        label: STAGE_LABELS[stage],
        count: data.companies.filter((company) => company.stage === stage).length,
      })),
    [data.companies]
  );

  const liveDeals = useMemo(() => {
    const liveStages = new Set([LeadStage.MEETING, LeadStage.PROPOSAL]);
    return data.companies
      .filter((company) => liveStages.has(company.stage))
      .map((company) => {
        const companyProposals = data.proposals.filter((proposal) => proposal.companyId === company.id);
        const latestProposal = companyProposals.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];
        const latestInteraction = latestDate(data.interactions.filter((interaction) => interaction.companyId === company.id).map((interaction) => interaction.date));
        const lastMovement = latestDate([latestInteraction, latestProposal?.date, company.createdAt]);
        return {
          company,
          proposalTotal: latestProposal?.total ?? 0,
          days: daysSince(lastMovement),
        };
      })
      .sort((a, b) => b.proposalTotal - a.proposalTotal || b.company.score - a.company.score);
  }, [data.companies, data.interactions, data.proposals]);

  const wonClients = useMemo(() => data.companies.filter((company) => company.stage === LeadStage.CLOSED_WON), [data.companies]);

  const weeklyChartData = useMemo(
    () =>
      data.weeklyMetrics.map((metric, index) => ({
        week: formatDate(metric.weekStart),
        empresas: metric.empresasContactadas,
        conversaciones: metric.conversaciones,
        posts: metric.posts,
        entreno: metric.entrenoDias,
        movil: metric.movilHoras,
        movilObjetivo: index === 0 ? 4 : index === 1 ? 3 : 2,
        autovaloracion: metric.autovaloracion,
        diagnosticos: metric.diagnosticos,
        retainers: metric.retainers,
      })),
    [data.weeklyMetrics]
  );

  const cashSummary = useMemo(() => {
    const totalCobrado = data.cashLedger.filter((entry) => entry.cobrado).reduce((sum, entry) => sum + entry.importe, 0);
    const facturado = data.cashLedger.filter((entry) => entry.tipo === 'FACTURADO').reduce((sum, entry) => sum + entry.importe, 0);
    const previsto = data.cashLedger.filter((entry) => entry.tipo === 'PREVISTO').reduce((sum, entry) => sum + entry.importe, 0);
    const cobrado = data.cashLedger.filter((entry) => entry.tipo === 'COBRADO' || entry.cobrado).reduce((sum, entry) => sum + entry.importe, 0);
    return {
      totalCobrado,
      facturado,
      previsto,
      cobrado,
      progress: Math.min(100, (totalCobrado / 3000) * 100),
      remaining: Math.max(0, 3000 - totalCobrado),
    };
  }, [data.cashLedger]);

  const renderContent = () => {
    if (activeSection === 'REDES') {
      return <SocialBoard workspaceId={workspaceId} reloadToken={reloadToken} />;
    }

    if (loading) return <LoadingState />;

    if (activeSection === 'GESTION') {
      if (gestionSubView === 'PROJECTS') {
        return (
          <ProjectsDashboard
            projects={data.projects}
            companyById={companyById}
            companies={data.companies}
            workspaceId={workspaceId}
            onRefresh={loadData}
          />
        );
      }
      if (gestionSubView === 'METRICS') {
        return <WeeklyMetricsView metrics={data.weeklyMetrics} chartData={weeklyChartData} />;
      }
      return <CashView ledger={data.cashLedger} companyById={companyById} summary={cashSummary} />;
    }

    if (activeSection === 'VENTAS') {
      if (ventasSubView === 'PRESUPUESTOS') {
        return (
          <React.Suspense fallback={<LoadingState />}>
            <Presupuestos workspaceId={workspaceId} companies={data.companies} />
          </React.Suspense>
        );
      }
      return null;
    }

    if (activeSection === 'CLIENTES') {
      if (clientesSubView === 'PIPELINE') {
        return (
          <PipelineView
            companies={filteredCompanies}
            funnel={funnel}
            liveDeals={liveDeals}
            canalFilter={canalFilter}
            setCanalFilter={setCanalFilter}
            sectorFilter={sectorFilter}
            setSectorFilter={setSectorFilter}
            sectors={sectors}
            contacts={data.contacts}
            interactions={data.interactions}
            workspaceId={workspaceId}
            onRefresh={loadData}
          />
        );
      }
      return (
        <ClientsView
          clients={wonClients}
          projects={data.projects}
          interactions={data.interactions}
          contacts={data.contacts}
          workspaceId={workspaceId}
          onRefresh={loadData}
        />
      );
    }

    return null;
  };

  const isContentView = activeSection === 'REDES';

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f2ec] font-sans selection:bg-[#FF9800] selection:text-black">
      <div className={`mx-auto flex min-h-screen w-full min-w-0 flex-col px-3.5 pb-36 pt-4 sm:px-6 lg:px-8 sm:pt-6 sm:pb-32 ${isContentView ? 'max-w-[1600px]' : 'max-w-7xl'}`}>
        
        {/* Header estilo Google con píldoras y esquinas redondeadas */}
        <header className="mb-4 sm:mb-6 flex items-start justify-between gap-3 sm:items-center">
          <div className="min-w-0 flex-1">
            <div className="mb-1.5 flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-[#FF9800]/30 bg-[#FF9800]/10 px-2.5 py-0.5 sm:px-3 sm:py-1 text-[11px] sm:text-xs font-semibold uppercase tracking-wider text-[#FF9800]">
                <Radio className="h-3 w-3" />
                {isContentView ? 'Redes' : 'SDAI Hub'}
              </span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-[#f5f2ec] sm:text-5xl">
              SDAI
            </h1>
            <p className="mt-0.5 text-xs text-[#f5f2ec]/60 sm:text-sm line-clamp-1 sm:line-clamp-none">
              {activeSection === 'GESTION' && 'Proyectos, métricas comerciales y objetivo de caja hacia la S.L.'}
              {activeSection === 'CLIENTES' && 'Pipeline de ventas, deals en curso y cuentas ganadas.'}
              {activeSection === 'VENTAS' && 'Presupuestos con formato fijo y PDF descargable.'}
              {activeSection === 'REDES' && 'Planificador y tablero multicanal para LinkedIn, Instagram y más.'}
            </p>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => {
                void loadData();
                setReloadToken((token) => token + 1);
              }}
              className="grid h-9 w-9 sm:h-11 sm:w-11 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-[#f5f2ec]/70 transition-all hover:border-[#FF9800]/60 hover:bg-[#FF9800]/10 hover:text-[#FF9800] active:scale-95"
              title="Actualizar datos"
            >
              <RefreshCw className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>
            <button
              type="button"
              onClick={onLogout}
              className="grid h-9 w-9 sm:h-11 sm:w-11 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-[#f5f2ec]/70 transition-all hover:border-[#FF9800]/60 hover:bg-[#FF9800]/10 hover:text-[#FF9800] active:scale-95"
              title={`Salir de ${user.name}`}
            >
              <LogOut className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            </button>
          </div>
        </header>

        {/* NAVEGACIÓN PRINCIPAL EN 3 PESTAÑAS (Estilo Google Pill) */}
        <div className="mb-5 sm:mb-6 flex flex-col gap-2.5">
          <div className="flex w-full items-center justify-center rounded-full border border-white/10 bg-white/[0.03] p-1 shadow-inner">
            <div className="grid w-full grid-cols-4 gap-1">
              <MainTabButton
                label="Gestión"
                icon={LayoutDashboard}
                badge={String(data.projects.length)}
                active={activeSection === 'GESTION'}
                onClick={() => setActiveSection('GESTION')}
              />
              <MainTabButton
                label="Clientes"
                icon={Users}
                badge={String(data.companies.length)}
                active={activeSection === 'CLIENTES'}
                onClick={() => setActiveSection('CLIENTES')}
              />
              <MainTabButton
                label="Ventas"
                icon={FileText}
                active={activeSection === 'VENTAS'}
                onClick={() => setActiveSection('VENTAS')}
              />
              <MainTabButton
                label="Redes"
                icon={Share2}
                active={activeSection === 'REDES'}
                onClick={() => setActiveSection('REDES')}
              />
            </div>
          </div>

          {/* SUB-NAVEGACIÓN EN PÍLDORAS PARA GESTIÓN (Con scroll horizontal táctil en móvil) */}
          {activeSection === 'GESTION' && (
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 px-0.5 sm:justify-center animate-in fade-in duration-200">
              <SubPillButton
                label="Proyectos"
                icon={FolderGit2}
                badge={String(data.projects.length)}
                active={gestionSubView === 'PROJECTS'}
                onClick={() => setGestionSubView('PROJECTS')}
              />
              <SubPillButton
                label="Métricas"
                icon={Activity}
                active={gestionSubView === 'METRICS'}
                onClick={() => setGestionSubView('METRICS')}
              />
              <SubPillButton
                label="Caja (3.000€)"
                icon={Banknote}
                badge={`${compactNumber.format(cashSummary.progress)}%`}
                active={gestionSubView === 'CASH'}
                onClick={() => setGestionSubView('CASH')}
              />
            </div>
          )}

          {/* SUB-NAVEGACIÓN EN PÍLDORAS PARA VENTAS */}
          {activeSection === 'VENTAS' && (
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 px-0.5 sm:justify-center animate-in fade-in duration-200">
              <SubPillButton
                label="Presupuestos"
                icon={FileText}
                active={ventasSubView === 'PRESUPUESTOS'}
                onClick={() => setVentasSubView('PRESUPUESTOS')}
              />
            </div>
          )}

          {/* SUB-NAVEGACIÓN EN PÍLDORAS PARA CLIENTES (Con scroll horizontal táctil en móvil) */}
          {activeSection === 'CLIENTES' && (
            <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5 px-0.5 sm:justify-center animate-in fade-in duration-200">
              <SubPillButton
                label="Pipeline"
                icon={Layers3}
                badge={String(filteredCompanies.length)}
                active={clientesSubView === 'PIPELINE'}
                onClick={() => setClientesSubView('PIPELINE')}
              />
              <SubPillButton
                label="Clientes Ganados"
                icon={CheckCircle2}
                badge={String(wonClients.length)}
                active={clientesSubView === 'CLIENTS'}
                onClick={() => setClientesSubView('CLIENTS')}
              />
            </div>
          )}
        </div>

        {/* Contenido Principal */}
        <main className="w-full min-w-0 flex-1">{renderContent()}</main>

        {/* Footer */}
        <footer className="mt-8 sm:mt-10 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4 text-[11px] sm:text-xs text-[#f5f2ec]/40">
          <span className="flex items-center gap-2">
            <Database className="h-3 w-3 text-[#FF9800]" />
            {isSupabaseConfigured() ? 'Supabase conectado' : 'Modo local'}
          </span>
          <span>{lastUpdated ? `Actualizado ${formatDate(lastUpdated)}` : 'Esperando datos'}</span>
        </footer>
      </div>

      {/* Navegación móvil flotante estilo Google en píldora con soporte para Home Bar de iOS */}
      <nav className="fixed inset-x-4 bottom-3 z-40 md:hidden pb-[max(0.25rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto flex max-w-sm items-center justify-around rounded-full border border-white/15 bg-[#121212]/95 p-1.5 shadow-2xl backdrop-blur-2xl">
          <MobileNavButton
            label="Gestión"
            icon={LayoutDashboard}
            active={activeSection === 'GESTION'}
            onClick={() => setActiveSection('GESTION')}
          />
          <MobileNavButton
            label="Clientes"
            icon={Users}
            active={activeSection === 'CLIENTES'}
            onClick={() => setActiveSection('CLIENTES')}
          />
          <MobileNavButton
            label="Ventas"
            icon={FileText}
            active={activeSection === 'VENTAS'}
            onClick={() => setActiveSection('VENTAS')}
          />
          <MobileNavButton
            label="Redes"
            icon={Share2}
            active={activeSection === 'REDES'}
            onClick={() => setActiveSection('REDES')}
          />
        </div>
      </nav>
    </div>
  );
};

const MainTabButton: React.FC<{
  label: string;
  icon: React.ElementType;
  badge?: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, icon: Icon, badge, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex h-10 sm:h-11 items-center justify-center gap-1.5 sm:gap-2 rounded-full px-2.5 sm:px-4 text-xs sm:text-sm font-semibold transition-all duration-200 active:scale-95 ${
      active
        ? 'bg-[#FF9800] text-black shadow-md'
        : 'text-[#f5f2ec]/60 hover:bg-white/[0.05] hover:text-[#f5f2ec]'
    }`}
  >
    <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0" />
    <span>{label}</span>
    {badge && (
      <span
        className={`hidden sm:inline-block ml-0.5 rounded-full px-1.5 py-0.2 text-[10px] sm:text-[11px] font-bold ${
          active ? 'bg-black/20 text-black' : 'bg-white/10 text-[#f5f2ec]/60'
        }`}
      >
        {badge}
      </span>
    )}
  </button>
);

const SubPillButton: React.FC<{
  label: string;
  icon: React.ElementType;
  badge?: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, icon: Icon, badge, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex h-8 sm:h-9 shrink-0 items-center gap-1.5 rounded-full border px-3 sm:px-4 text-[11px] sm:text-xs font-semibold transition-all active:scale-95 ${
      active
        ? 'border-white/20 bg-white text-black shadow-sm'
        : 'border-white/10 bg-white/[0.02] text-[#f5f2ec]/60 hover:border-white/20 hover:text-[#f5f2ec]'
    }`}
  >
    <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
    <span>{label}</span>
    {badge && (
      <span
        className={`rounded-full px-1.5 py-0.2 text-[9px] sm:text-[10px] font-bold ${
          active ? 'bg-black/15 text-black' : 'bg-white/10 text-[#f5f2ec]/50'
        }`}
      >
        {badge}
      </span>
    )}
  </button>
);

const MobileNavButton: React.FC<{
  label: string;
  icon: React.ElementType;
  active: boolean;
  onClick: () => void;
}> = ({ label, icon: Icon, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-col items-center justify-center rounded-full px-4 py-1.5 transition-all active:scale-95 ${
      active ? 'bg-[#FF9800] text-black font-bold shadow-md' : 'text-[#f5f2ec]/60'
    }`}
  >
    <Icon className="h-4 w-4" />
    <span className="text-[10px] mt-0.5 font-medium">{label}</span>
  </button>
);

const LoadingState = () => (
  <div className="grid min-h-[40vh] place-items-center rounded-3xl border border-white/10 bg-white/[0.02]">
    <div className="text-center">
      <RefreshCw className="mx-auto mb-3 h-7 w-7 animate-spin text-[#FF9800]" />
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#f5f2ec]/50">
        Cargando datos…
      </p>
    </div>
  </div>
);

const EmptyState: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div className="rounded-2xl sm:rounded-3xl border border-dashed border-white/15 bg-white/[0.015] px-4 py-8 sm:px-6 sm:py-12 text-center">
    <p className="text-base sm:text-xl font-bold text-[#f5f2ec]">{title}</p>
    <p className="mx-auto mt-1.5 max-w-md text-xs sm:text-sm leading-relaxed text-[#f5f2ec]/50">{body}</p>
  </div>
);

const MetricCard: React.FC<{
  label: string;
  value: string;
  hint?: string;
  icon: React.ElementType;
  className?: string;
}> = ({ label, value, hint, icon: Icon, className = '' }) => (
  <div className={`rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.025] p-3.5 sm:p-5 shadow-sm transition hover:border-white/20 ${className}`}>
    <div className="mb-2 sm:mb-4 flex items-center justify-between text-[#f5f2ec]/50">
      <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider">{label}</span>
      <Icon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-[#FF9800]" />
    </div>
    <div className="text-2xl sm:text-3xl font-bold tracking-tight text-[#f5f2ec]">{value}</div>
    {hint && <div className="mt-1 text-[10px] sm:text-xs text-[#f5f2ec]/40">{hint}</div>}
  </div>
);

/* ========================================================================= */
/* VISTA DE PROYECTOS — gestión sencilla estilo Plane/Leantime/Kanboard   */
/* Referencias: Plane (states + cycles minimalistas), Leantime (goals     */
/* para equipos pequeños), Kanboard (kanban mínimo, táctil 44px).         */
/* Todo operable desde móvil: crear, cambiar estado, +/− progreso.        */
/* ========================================================================= */
const PROJECT_STATUSES: { value: ProjectStatus; label: string }[] = [
  { value: ProjectStatus.PLANNING, label: 'Planificado' },
  { value: ProjectStatus.ACTIVE, label: 'En curso' },
  { value: ProjectStatus.ON_HOLD, label: 'En pausa' },
  { value: ProjectStatus.COMPLETED, label: 'Completado' },
];

const STATUS_STYLES: Record<ProjectStatus, string> = {
  [ProjectStatus.PLANNING]: 'border-white/20 bg-white/[0.06] text-[#f5f2ec]/70',
  [ProjectStatus.ACTIVE]: 'border-[#FF9800]/40 bg-[#FF9800]/10 text-[#FF9800]',
  [ProjectStatus.ON_HOLD]: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  [ProjectStatus.COMPLETED]: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
};

const newProjectDraft = (workspaceId: string): Project => ({
  id: crypto.randomUUID(),
  workspaceId,
  name: '',
  description: '',
  status: ProjectStatus.PLANNING,
  startDate: new Date().toISOString().slice(0, 10),
  endDate: undefined,
  clientCompanyId: undefined,
  progress: 0,
});

const ProjectsDashboard: React.FC<{
  projects: Project[];
  companyById: Map<string, Company>;
  companies: Company[];
  workspaceId: string;
  onRefresh: () => Promise<void>;
}> = ({ projects, companyById, companies, workspaceId, onRefresh }) => {
  const [statusFilter, setStatusFilter] = useState<'ALL' | ProjectStatus>('ALL');
  const [search, setSearch] = useState('');
  const [editor, setEditor] = useState<Project | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const activeCount = projects.filter((p) => p.status === ProjectStatus.ACTIVE).length;
  const completedCount = projects.filter((p) => p.status === ProjectStatus.COMPLETED).length;
  const averageProgress = projects.length
    ? Math.round(projects.reduce((acc, p) => acc + (p.progress ?? 0), 0) / projects.length)
    : 0;

  const filtered = useMemo(
    () =>
      projects.filter((p) => {
        const matchesStatus = statusFilter === 'ALL' || p.status === statusFilter;
        const q = search.trim().toLowerCase();
        const company = p.clientCompanyId ? companyById.get(p.clientCompanyId) : undefined;
        const matchesSearch =
          !q ||
          p.name.toLowerCase().includes(q) ||
          (company?.name ?? '').toLowerCase().includes(q);
        return matchesStatus && matchesSearch;
      }),
    [projects, statusFilter, search, companyById]
  );

  const persistProject = async (project: Project) => {
    if (!supabase) {
      throw new Error('Sin conexión a Supabase. Revisa VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY.');
    }
    const payload = {
      id: project.id,
      workspace_id: workspaceId,
      company_id: project.clientCompanyId ?? null,
      name: project.name.trim() || 'Sin título',
      description: project.description || null,
      status: project.status,
      progress: Math.max(0, Math.min(100, Math.round(project.progress ?? 0))),
      start_date: project.startDate || null,
      end_date: project.endDate || null,
    };
    const { error: writeError } = await supabase.from('projects').upsert(payload);
    if (writeError) throw new Error(`Guardar falló: ${writeError.message}`);
    await onRefresh();
  };

  const handleQuickProgress = async (project: Project, delta: number) => {
    setBusyId(project.id);
    setError(null);
    const next = {
      ...project,
      progress: Math.max(0, Math.min(100, (project.progress ?? 0) + delta)),
      status:
        (project.progress ?? 0) + delta >= 100 ? ProjectStatus.COMPLETED : project.status === ProjectStatus.COMPLETED && delta < 0 ? ProjectStatus.ACTIVE : project.status,
    };
    try {
      await persistProject(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo actualizar el progreso.');
    } finally {
      setBusyId(null);
    }
  };

  const handleQuickStatus = async (project: Project, status: ProjectStatus) => {
    if (project.status === status) return;
    setBusyId(project.id);
    setError(null);
    const next: Project = {
      ...project,
      status,
      progress: status === ProjectStatus.COMPLETED ? 100 : project.progress,
    };
    try {
      await persistProject(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar el estado.');
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveEditor = async () => {
    if (!editor) return;
    if (!editor.name.trim()) {
      setError('Pon un nombre al proyecto.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await persistProject(editor);
      setEditor(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el proyecto.');
    }
    setSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (!supabase) {
      setError('Sin conexión a Supabase. No se puede borrar.');
      return;
    }
    setDeleting(true);
    setError(null);
    try {
      // .select() para detectar RLS: si devuelve 0 filas, no se borró (sin permiso o no existe).
      const { data, error: delError } = await supabase.from('projects').delete().eq('id', id).select('id');
      if (delError) throw new Error(`Borrar falló: ${delError.message}`);
      if (!data || data.length === 0) {
        throw new Error('No se borró ninguna fila. Revisa las políticas RLS de DELETE en la tabla projects.');
      }
      setConfirmDelete(false);
      setEditor(null);
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="space-y-4 sm:space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#f5f2ec]">Proyectos</h2>
          <p className="text-xs sm:text-sm text-[#f5f2ec]/50">
            Simple como Kanboard, con estados tipo Plane. Todo táctil.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setConfirmDelete(false);
            setEditor(newProjectDraft(workspaceId));
          }}
          className="inline-flex min-h-[48px] shrink-0 items-center gap-2 rounded-full bg-[#FF9800] px-5 text-sm font-bold text-black shadow-md transition active:scale-95"
        >
          <Plus className="h-5 w-5" />
          Nuevo
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <MetricCard label="En curso" value={String(activeCount)} hint="ACTIVE" icon={Briefcase} />
        <MetricCard label="Completados" value={String(completedCount)} hint="COMPLETED" icon={CheckCircle2} />
        <MetricCard
          label="Progreso medio"
          value={`${averageProgress}%`}
          hint="Media global"
          icon={TrendingUp}
          className="col-span-2 sm:col-span-1"
        />
      </div>

      {/* Filtros + búsqueda: scroll horizontal en móvil, inputs 44px+ */}
      <div className="flex flex-col gap-2 rounded-2xl border border-white/10 bg-white/[0.025] p-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#f5f2ec]/40" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por proyecto o cliente…"
            className="h-11 w-full rounded-xl border border-white/10 bg-black/40 pl-10 pr-3 text-sm text-[#f5f2ec] outline-none placeholder:text-[#f5f2ec]/30 focus:border-[#FF9800]"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
          <button
            type="button"
            onClick={() => setStatusFilter('ALL')}
            className={`h-11 shrink-0 rounded-full px-4 text-xs font-semibold transition active:scale-95 ${statusFilter === 'ALL' ? 'bg-[#f5f2ec] text-black' : 'border border-white/10 text-[#f5f2ec]/60'}`}
          >
            Todos ({projects.length})
          </button>
          {PROJECT_STATUSES.map((s) => {
            const count = projects.filter((p) => p.status === s.value).length;
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => setStatusFilter(s.value)}
                className={`h-11 shrink-0 rounded-full border px-4 text-xs font-semibold transition active:scale-95 ${statusFilter === s.value ? 'border-[#FF9800] bg-[#FF9800]/15 text-[#FF9800]' : 'border-white/10 text-[#f5f2ec]/60'}`}
              >
                {s.label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs sm:text-sm text-red-300">
          {error}
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="rounded-2xl sm:rounded-3xl border border-dashed border-white/15 bg-white/[0.015] px-4 py-8 sm:px-6 sm:py-12 text-center">
          <p className="text-base sm:text-xl font-bold text-[#f5f2ec]">
            {projects.length === 0 ? 'Sin proyectos activos' : 'Sin resultados'}
          </p>
          <p className="mx-auto mt-1.5 max-w-md text-xs sm:text-sm leading-relaxed text-[#f5f2ec]/50">
            {projects.length === 0
              ? 'Crea tu primer proyecto con el botón Nuevo. Solo pide nombre, cliente, estado y progreso.'
              : 'Prueba con otro filtro o búsqueda.'}
          </p>
          {projects.length === 0 && (
            <button
              type="button"
              onClick={() => {
                setError(null);
                setConfirmDelete(false);
                setEditor(newProjectDraft(workspaceId));
              }}
              className="mx-auto mt-4 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-[#FF9800] px-6 text-sm font-bold text-black active:scale-95"
            >
              <Plus className="h-5 w-5" />
              Crear proyecto
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((project) => {
            const company = project.clientCompanyId ? companyById.get(project.clientCompanyId) : undefined;
            const progress = Math.max(0, Math.min(100, project.progress ?? 0));

            return (
              <article
                key={project.id}
                className="flex flex-col justify-between rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.025] p-4 sm:p-5 shadow-sm transition hover:border-[#FF9800]/50"
              >
                <div>
                  <div className="mb-2.5 flex items-start justify-between gap-2">
                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[project.status]}`}>
                      {PROJECT_STATUSES.find((s) => s.value === project.status)?.label ?? project.status}
                    </span>
                    <span className="text-lg sm:text-xl font-bold text-[#FF9800]">{progress}%</span>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setError(null);
                      setConfirmDelete(false);
                      setEditor(project);
                    }}
                    className="text-left"
                  >
                    <h3 className="text-lg sm:text-xl font-bold leading-tight text-[#f5f2ec] underline-offset-4 hover:underline">{project.name}</h3>
                  </button>
                  <p className="mt-1 text-xs sm:text-sm font-medium text-[#f5f2ec]/60">
                    {company ? (
                      <>Cliente: <span className="text-[#f5f2ec]">{company.name}</span></>
                    ) : (
                      <span className="text-[#f5f2ec]/40">Sin cliente asignado</span>
                    )}
                  </p>

                  {project.description && (
                    <p className="mt-2 line-clamp-3 text-xs sm:text-sm leading-relaxed text-[#f5f2ec]/50">
                      {project.description}
                    </p>
                  )}
                </div>

                <div className="mt-4 space-y-3">
                  <div className="h-2.5 w-full rounded-full bg-white/[0.08] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#FF9800] transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>

                  {/* Control rápido de progreso: botones 44px para pulgar */}
                  <div className="flex items-center justify-between gap-2">
                    <button
                      type="button"
                      onClick={() => void handleQuickProgress(project, -10)}
                      disabled={busyId === project.id}
                      aria-label="Quitar 10 por ciento"
                      className="grid h-11 w-11 place-items-center rounded-full border border-white/10 bg-white/[0.04] text-lg font-bold text-[#f5f2ec]/70 active:scale-95 disabled:opacity-40"
                    >
                      −
                    </button>
                    <span className="text-[11px] text-[#f5f2ec]/40">
                      {busyId === project.id ? 'Guardando…' : '−10 / +10'}
                    </span>
                    <button
                      type="button"
                      onClick={() => void handleQuickProgress(project, 10)}
                      disabled={busyId === project.id}
                      aria-label="Añadir 10 por ciento"
                      className="grid h-11 w-11 place-items-center rounded-full border border-[#FF9800]/40 bg-[#FF9800]/10 text-lg font-bold text-[#FF9800] active:scale-95 disabled:opacity-40"
                    >
                      +
                    </button>
                  </div>

                  {/* Cambio de estado en 1 toque */}
                  <div className="flex gap-1.5 overflow-x-auto no-scrollbar">
                    {PROJECT_STATUSES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => void handleQuickStatus(project, s.value)}
                        disabled={busyId === project.id}
                        className={`h-10 shrink-0 rounded-full border px-3 text-[11px] font-semibold transition active:scale-95 disabled:opacity-40 ${project.status === s.value ? 'border-[#FF9800] bg-[#FF9800]/15 text-[#FF9800]' : 'border-white/10 text-[#f5f2ec]/50'}`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>

                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[11px] text-[#f5f2ec]/40">
                      <span>Inicio: {formatDate(project.startDate)}</span>
                      {project.endDate && <span> · Fin: {formatDate(project.endDate)}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        setConfirmDelete(false);
                        setEditor(project);
                      }}
                      className="inline-flex min-h-[44px] items-center rounded-full border border-white/10 px-4 text-xs font-semibold text-[#f5f2ec]/70 active:scale-95 hover:border-white/25 hover:text-white"
                    >
                      Abrir
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Editor bottom-sheet móvil, centrado en desktop */}
      {editor && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/80 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4">
          <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-3xl border-t border-white/15 bg-[#111] p-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded-3xl sm:border sm:p-6">
            <div className="mx-auto mb-2 h-1 w-12 rounded-full bg-white/20 sm:hidden" />
            <div className="mb-4 flex items-start justify-between gap-3 border-b border-white/10 pb-3">
              <div>
                <span className="rounded-full bg-[#FF9800]/15 px-2.5 py-0.5 text-[10px] font-semibold text-[#FF9800]">
                  {projects.some((p) => p.id === editor.id) ? 'Editar proyecto' : 'Nuevo proyecto'}
                </span>
                <h3 className="mt-1 text-xl font-bold text-[#f5f2ec]">Proyecto simple</h3>
                <p className="text-xs text-[#f5f2ec]/50">Nombre + cliente + estado + progreso. Nada más.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setConfirmDelete(false);
                  setEditor(null);
                }}
                aria-label="Cerrar editor"
                className="grid h-11 w-11 place-items-center rounded-full border border-white/10 text-[#f5f2ec]/60"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 space-y-3.5 overflow-y-auto pr-0.5">
              <label className="block">
                <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Nombre *</span>
                <input
                  value={editor.name}
                  onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                  placeholder="Ej: Web + automatización Clínica X"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-4 text-[16px] text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                />
              </label>

              <label className="block">
                <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Cliente</span>
                <select
                  value={editor.clientCompanyId ?? ''}
                  onChange={(e) => setEditor({ ...editor, clientCompanyId: e.target.value || undefined })}
                  className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none"
                >
                  <option value="" className="bg-[#1a1a1a]">Sin cliente</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id} className="bg-[#1a1a1a]">{c.name}</option>
                  ))}
                </select>
              </label>

              <div>
                <span className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Estado</span>
                <div className="grid grid-cols-2 gap-2">
                  {PROJECT_STATUSES.map((s) => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => setEditor({ ...editor, status: s.value, progress: s.value === ProjectStatus.COMPLETED ? 100 : editor.progress })}
                      className={`min-h-[48px] rounded-2xl border px-3 text-sm font-semibold transition active:scale-95 ${editor.status === s.value ? 'border-[#FF9800] bg-[#FF9800]/15 text-[#FF9800]' : 'border-white/10 text-[#f5f2ec]/60'}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Progreso</span>
                  <span className="text-xl font-bold text-[#FF9800]">{Math.round(editor.progress ?? 0)}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  step={5}
                  value={Math.round(editor.progress ?? 0)}
                  onChange={(e) => setEditor({ ...editor, progress: Number(e.target.value) })}
                  className="h-11 w-full accent-[#FF9800]"
                />
                <div className="flex gap-2">
                  {[0, 25, 50, 75, 100].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setEditor({ ...editor, progress: v, status: v === 100 ? ProjectStatus.COMPLETED : editor.status === ProjectStatus.COMPLETED ? ProjectStatus.ACTIVE : editor.status })}
                      className="h-10 flex-1 rounded-full border border-white/10 text-xs font-semibold text-[#f5f2ec]/60 active:scale-95"
                    >
                      {v}%
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Inicio</span>
                  <input
                    type="date"
                    value={editor.startDate?.slice(0, 10) ?? ''}
                    onChange={(e) => setEditor({ ...editor, startDate: e.target.value })}
                    className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none"
                  />
                </label>
                <label className="block">
                  <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Fin (opcional)</span>
                  <input
                    type="date"
                    value={editor.endDate?.slice(0, 10) ?? ''}
                    onChange={(e) => setEditor({ ...editor, endDate: e.target.value || undefined })}
                    className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none"
                  />
                </label>
              </div>

              <label className="block">
                <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Notas (opcional)</span>
                <textarea
                  value={editor.description ?? ''}
                  onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  rows={3}
                  placeholder="Alcance en 1-2 líneas…"
                  className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-[16px] leading-relaxed text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                />
              </label>
            </div>

            <div className="mt-4 flex items-center justify-between gap-2 border-t border-white/10 pt-3">
              {projects.some((p) => p.id === editor.id) ? (
                confirmDelete ? (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => void handleDelete(editor.id)}
                      disabled={deleting}
                      className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full bg-red-500 px-4 text-sm font-bold text-white active:scale-95 disabled:opacity-60"
                    >
                      <Trash2 className="h-4 w-4" />
                      {deleting ? 'Borrando…' : 'Confirmar'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(false)}
                      disabled={deleting}
                      className="min-h-[48px] rounded-full border border-white/10 px-4 text-sm font-semibold text-[#f5f2ec]/60 active:scale-95"
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmDelete(true)}
                    disabled={saving || deleting}
                    className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-red-500/30 px-4 text-sm font-semibold text-red-300 active:scale-95 disabled:opacity-60"
                  >
                    <Trash2 className="h-4 w-4" />
                    Borrar
                  </button>
                )
              ) : (
                <span />
              )}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setConfirmDelete(false);
                    setEditor(null);
                  }}
                  className="min-h-[48px] rounded-full border border-white/10 px-5 text-sm font-semibold text-[#f5f2ec]/60 active:scale-95"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => void handleSaveEditor()}
                  disabled={saving}
                  className="inline-flex min-h-[48px] items-center rounded-full bg-[#FF9800] px-6 text-sm font-bold text-black active:scale-95 disabled:opacity-60"
                >
                  {saving ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

/* ========================================================================= */
/* CRM INTERACTIVO — helpers + editor compartido Pipeline/Clientes         */
/* Patrón Plane-simple: una ficha por empresa con Datos/Contactos/Actividad*/
/* Todo táctil 44px+, bottom-sheet en móvil.                               */
/* ========================================================================= */
const newCompanyDraft = (workspaceId: string): Company => ({
  id: crypto.randomUUID(),
  workspaceId,
  name: '',
  sector: '',
  stage: LeadStage.NEW,
  score: 5,
  createdAt: new Date().toISOString(),
  followUpAttempts: 0,
  ownerId: '',
  website: undefined,
  notes: undefined,
  canal: undefined,
  region: undefined,
  employeeCount: undefined,
  linkedinUrl: undefined,
  source: undefined,
  nextContactAt: undefined,
});

const persistCompany = async (supabaseClient: any, workspaceId: string, company: Company) => {
  if (!supabaseClient) throw new Error('Sin conexión a Supabase.');
  const full: Record<string, any> = {
    id: company.id,
    workspace_id: workspaceId,
    name: company.name.trim() || 'Sin nombre',
    sector: company.sector || null,
    stage: company.stage,
    score: Math.max(0, Math.min(10, Math.round(company.score ?? 0))),
    next_contact_at: company.nextContactAt || null,
    website: company.website || null,
    notes: company.notes || null,
    canal: company.canal || null,
    linkedin_url: company.linkedinUrl || null,
    region: company.region || null,
    employee_count: company.employeeCount || null,
    source: company.source || null,
  };
  const { error } = await supabaseClient.from('companies').upsert(full);
  if (!error) return;
  const msg = (error.message || '').toLowerCase();
  // Esquema antiguo sin columnas extra: reintentar con el mínimo (como hace SocialBoard con notes).
  if (msg.includes('canal') || msg.includes('linkedin') || msg.includes('region') || msg.includes('employee') || msg.includes('source') || (error as any).code === 'PGRST204' || (error as any).code === '42703') {
    const minimal = {
      id: full.id,
      workspace_id: full.workspace_id,
      name: full.name,
      sector: full.sector,
      stage: full.stage,
      score: full.score,
      next_contact_at: full.next_contact_at,
      website: full.website,
      notes: full.notes,
    };
    const { error: retryError } = await supabaseClient.from('companies').upsert(minimal);
    if (retryError) throw new Error(`Guardar lead falló: ${retryError.message}`);
    return;
  }
  throw new Error(`Guardar lead falló: ${error.message}`);
};

const deleteCompanyRow = async (supabaseClient: any, id: string) => {
  if (!supabaseClient) throw new Error('Sin conexión a Supabase.');
  const { data, error } = await supabaseClient.from('companies').delete().eq('id', id).select('id');
  if (error) throw new Error(`Borrar falló: ${error.message}`);
  if (!data || data.length === 0) throw new Error('No se borró ninguna fila. Revisa RLS de DELETE en companies.');
};

const INTERACTION_TYPES: { value: Interaction['type']; label: string }[] = [
  { value: 'CALL', label: 'Llamada' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'MEETING', label: 'Reunión' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
];

const CANAL_OPTIONS: { value: LeadCanal; label: string }[] = [
  { value: 'COLD_EMAIL', label: 'Email' },
  { value: 'LINKEDIN', label: 'LinkedIn' },
  { value: 'PHONE', label: 'Teléfono' },
];

const CompanyEditorModal: React.FC<{
  draft: Company;
  setDraft: (c: Company) => void;
  contacts: Contact[];
  interactions: Interaction[];
  isNew: boolean;
  saving: boolean;
  deleting: boolean;
  confirmDelete: boolean;
  setConfirmDelete: (v: boolean) => void;
  error: string | null;
  tab: 'DATOS' | 'CONTACTOS' | 'ACTIVIDAD';
  setTab: (t: 'DATOS' | 'CONTACTOS' | 'ACTIVIDAD') => void;
  newContact: { name: string; email: string; role: string };
  setNewContact: (v: { name: string; email: string; role: string }) => void;
  newInteraction: { type: Interaction['type']; notes: string; nextSteps: string };
  setNewInteraction: (v: { type: Interaction['type']; notes: string; nextSteps: string }) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
  onAddContact: () => void;
  onDeleteContact: (id: string) => void;
  onAddInteraction: () => void;
  onDeleteInteraction: (id: string) => void;
}> = ({
  draft, setDraft, contacts, interactions, isNew, saving, deleting, confirmDelete, setConfirmDelete,
  error, tab, setTab, newContact, setNewContact, newInteraction, setNewInteraction,
  onClose, onSave, onDelete, onAddContact, onDeleteContact, onAddInteraction, onDeleteInteraction,
}) => (
  <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/80 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4">
    <div className="flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-3xl border-t border-white/15 bg-[#111] p-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:rounded-3xl sm:border sm:p-6">
      <div className="mx-auto mb-2 h-1 w-12 rounded-full bg-white/20 sm:hidden" />
      <div className="mb-3 flex items-start justify-between gap-3 border-b border-white/10 pb-3">
        <div>
          <span className="rounded-full bg-[#FF9800]/15 px-2.5 py-0.5 text-[10px] font-semibold text-[#FF9800]">
            {isNew ? 'Nuevo lead' : 'Editar lead'}
          </span>
          <h3 className="mt-1 truncate text-xl font-bold text-[#f5f2ec]">{draft.name || 'Sin nombre'}</h3>
          <p className="text-xs text-[#f5f2ec]/50">{STAGE_LABELS[draft.stage] ?? draft.stage} · Score {draft.score}/10</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar ficha"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-white/10 text-[#f5f2ec]/60"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="mb-3 grid grid-cols-3 gap-1.5 rounded-2xl border border-white/10 bg-white/[0.02] p-1.5">
        {(['DATOS', 'CONTACTOS', 'ACTIVIDAD'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`min-h-[44px] rounded-xl text-xs font-bold transition active:scale-95 ${tab === t ? 'bg-[#FF9800] text-black' : 'text-[#f5f2ec]/60'}`}
          >
            {t === 'DATOS' ? `Datos` : t === 'CONTACTOS' ? `Contactos (${contacts.length})` : `Actividad (${interactions.length})`}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-3 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300">{error}</div>
      )}

      <div className="flex-1 space-y-3.5 overflow-y-auto pr-0.5">
        {tab === 'DATOS' && (
          <>
            <label className="block">
              <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Empresa *</span>
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Ej: Clínica Dental Norte"
                className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-4 text-[16px] text-[#f5f2ec] outline-none focus:border-[#FF9800]"
              />
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Sector</span>
                <input
                  value={draft.sector}
                  onChange={(e) => setDraft({ ...draft, sector: e.target.value })}
                  placeholder="Ej: Salud"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-4 text-[16px] text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                />
              </label>
              <label className="block">
                <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Web</span>
                <input
                  value={draft.website ?? ''}
                  onChange={(e) => setDraft({ ...draft, website: e.target.value || undefined })}
                  placeholder="https://…"
                  inputMode="url"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-4 text-[16px] text-[#f5f2ec] outline-none focus:border-[#FF9800]"
                />
              </label>
            </div>
            <div>
              <span className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Fase</span>
              <div className="grid grid-cols-2 gap-2">
                {STAGE_ORDER.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setDraft({ ...draft, stage: s })}
                    className={`min-h-[48px] rounded-2xl border px-3 text-xs font-bold transition active:scale-95 ${draft.stage === s ? 'border-[#FF9800] bg-[#FF9800]/15 text-[#FF9800]' : 'border-white/10 text-[#f5f2ec]/60'}`}
                  >
                    {STAGE_LABELS[s] ?? s}
                  </button>
                ))}
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/20 p-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Score</span>
                <span className="text-xl font-bold text-[#FF9800]">{draft.score}/10</span>
              </div>
              <input
                type="range" min={0} max={10} step={1} value={draft.score}
                onChange={(e) => setDraft({ ...draft, score: Number(e.target.value) })}
                className="h-11 w-full accent-[#FF9800]"
              />
            </div>
            <div>
              <span className="mb-1.5 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Canal</span>
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                <button
                  type="button"
                  onClick={() => setDraft({ ...draft, canal: undefined })}
                  className={`h-11 shrink-0 rounded-full border px-4 text-xs font-semibold active:scale-95 ${!draft.canal ? 'bg-[#f5f2ec] text-black' : 'border-white/10 text-[#f5f2ec]/60'}`}
                >
                  Sin canal
                </button>
                {CANAL_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setDraft({ ...draft, canal: c.value })}
                    className={`h-11 shrink-0 rounded-full border px-4 text-xs font-semibold active:scale-95 ${draft.canal === c.value ? 'border-[#FF9800] bg-[#FF9800]/15 text-[#FF9800]' : 'border-white/10 text-[#f5f2ec]/60'}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Región</span>
                <input
                  value={draft.region ?? ''}
                  onChange={(e) => setDraft({ ...draft, region: e.target.value || undefined })}
                  placeholder="Ej: Madrid"
                  className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-4 text-[16px] text-[#f5f2ec] outline-none"
                />
              </label>
              <label className="block">
                <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Próx. contacto</span>
                <input
                  type="date"
                  value={draft.nextContactAt?.slice(0, 10) ?? ''}
                  onChange={(e) => setDraft({ ...draft, nextContactAt: e.target.value || undefined })}
                  className="h-12 w-full rounded-2xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none"
                />
              </label>
            </div>
            <label className="block">
              <span className="mb-1 ml-1 block text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Notas</span>
              <textarea
                value={draft.notes ?? ''}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                rows={3}
                placeholder="Dolor, oportunidad, icebreaker…"
                className="w-full rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-[16px] text-[#f5f2ec] outline-none focus:border-[#FF9800]"
              />
            </label>
          </>
        )}

        {tab === 'CONTACTOS' && (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="py-2 text-center text-xs text-[#f5f2ec]/40">Sin contactos. Añade el primero abajo.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 rounded-2xl border border-white/10 bg-black/30 p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-[#f5f2ec]">{c.name} {c.isPrimary && <span className="ml-1 rounded-full bg-[#FF9800]/15 px-2 py-0.5 text-[10px] text-[#FF9800]">Principal</span>}</p>
                  <p className="truncate text-xs text-[#f5f2ec]/50">{[c.role, c.email].filter(Boolean).join(' · ') || 'Sin datos'}</p>
                </div>
                <button
                  type="button" onClick={() => onDeleteContact(c.id)} aria-label={`Borrar ${c.name}`}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-red-500/30 text-red-300 active:scale-95"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div className="rounded-2xl border border-dashed border-white/15 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Nuevo contacto</p>
              <div className="space-y-2">
                <input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Nombre *" className="h-12 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none focus:border-[#FF9800]" />
                <div className="grid gap-2 sm:grid-cols-2">
                  <input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email" inputMode="email" className="h-12 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none" />
                  <input value={newContact.role} onChange={(e) => setNewContact({ ...newContact, role: e.target.value })} placeholder="Cargo" className="h-12 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none" />
                </div>
                <button type="button" onClick={onAddContact} className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-white px-4 text-sm font-bold text-black active:scale-95">
                  <Plus className="h-4 w-4" /> Añadir contacto
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'ACTIVIDAD' && (
          <div className="space-y-3">
            {interactions.length === 0 && <p className="py-2 text-center text-xs text-[#f5f2ec]/40">Sin actividad. Registra la primera abajo.</p>}
            {interactions.slice(0, 10).map((i) => (
              <div key={i.id} className="rounded-2xl border-l-2 border-[#FF9800] bg-white/[0.02] p-3">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#FF9800]">{i.type} · {formatDate(i.date)}</p>
                  <button type="button" onClick={() => onDeleteInteraction(i.id)} aria-label="Borrar interacción" className="grid h-9 w-9 place-items-center rounded-full border border-white/10 text-[#f5f2ec]/40 active:scale-95">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
                <p className="mt-1 text-sm text-[#f5f2ec]/80">{i.notes || 'Sin notas'}</p>
                {i.nextSteps && <p className="mt-1 text-xs text-[#f5f2ec]/50">→ {i.nextSteps}</p>}
              </div>
            ))}
            <div className="rounded-2xl border border-dashed border-white/15 p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Nueva nota de actividad</p>
              <div className="mb-2 flex gap-1.5 overflow-x-auto no-scrollbar">
                {INTERACTION_TYPES.map((t) => (
                  <button key={t.value} type="button" onClick={() => setNewInteraction({ ...newInteraction, type: t.value })} className={`h-11 shrink-0 rounded-full border px-4 text-xs font-semibold active:scale-95 ${newInteraction.type === t.value ? 'border-[#FF9800] bg-[#FF9800]/15 text-[#FF9800]' : 'border-white/10 text-[#f5f2ec]/60'}`}>
                    {t.label}
                  </button>
                ))}
              </div>
              <textarea value={newInteraction.notes} onChange={(e) => setNewInteraction({ ...newInteraction, notes: e.target.value })} rows={2} placeholder="Qué pasó / qué se dijo… *" className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2.5 text-[16px] text-[#f5f2ec] outline-none focus:border-[#FF9800]" />
              <input value={newInteraction.nextSteps} onChange={(e) => setNewInteraction({ ...newInteraction, nextSteps: e.target.value })} placeholder="Siguiente paso (opcional)" className="mt-2 h-12 w-full rounded-xl border border-white/10 bg-black/40 px-3 text-[16px] text-[#f5f2ec] outline-none" />
              <button type="button" onClick={onAddInteraction} className="mt-2 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full bg-white px-4 text-sm font-bold text-black active:scale-95">
                <Plus className="h-4 w-4" /> Registrar
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-white/10 pt-3">
        {!isNew ? (
          confirmDelete ? (
            <div className="flex items-center gap-2">
              <button type="button" onClick={onDelete} disabled={deleting} className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full bg-red-500 px-4 text-sm font-bold text-white active:scale-95 disabled:opacity-60">
                <Trash2 className="h-4 w-4" /> {deleting ? 'Borrando…' : 'Confirmar'}
              </button>
              <button type="button" onClick={() => setConfirmDelete(false)} disabled={deleting} className="min-h-[48px] rounded-full border border-white/10 px-4 text-sm font-semibold text-[#f5f2ec]/60 active:scale-95">No</button>
            </div>
          ) : (
            <button type="button" onClick={() => setConfirmDelete(true)} disabled={saving || deleting} className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full border border-red-500/30 px-4 text-sm font-semibold text-red-300 active:scale-95">
              <Trash2 className="h-4 w-4" /> Borrar
            </button>
          )
        ) : (<span />)}
        <div className="flex items-center gap-2">
          <button type="button" onClick={onClose} className="min-h-[48px] rounded-full border border-white/10 px-5 text-sm font-semibold text-[#f5f2ec]/60 active:scale-95">Cerrar</button>
          <button type="button" onClick={onSave} disabled={saving || deleting} className="inline-flex min-h-[48px] items-center rounded-full bg-[#FF9800] px-6 text-sm font-bold text-black active:scale-95 disabled:opacity-60">
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>
  </div>
);

/* ========================================================================= */
/* VISTA PIPELINE (DENTRO DE CLIENTES) — interactiva                         */
/* ========================================================================= */
const PipelineView: React.FC<{
  companies: Company[];
  funnel: { stage: LeadStage; label: string; count: number }[];
  liveDeals: { company: Company; proposalTotal: number; days: number | null }[];
  canalFilter: 'ALL' | LeadCanal | 'UNKNOWN';
  setCanalFilter: (value: 'ALL' | LeadCanal | 'UNKNOWN') => void;
  sectorFilter: string;
  setSectorFilter: (value: string) => void;
  sectors: string[];
  contacts: Contact[];
  interactions: Interaction[];
  workspaceId: string;
  onRefresh: () => Promise<void>;
}> = ({ companies, funnel, liveDeals, canalFilter, setCanalFilter, sectorFilter, setSectorFilter, sectors, contacts, interactions, workspaceId, onRefresh }) => {
  const total = funnel.reduce((sum, item) => sum + item.count, 0);
  const averageScore = companies.length
    ? companies.reduce((sum, company) => sum + company.score, 0) / companies.length
    : 0;
  const maxStageCount = Math.max(1, ...funnel.map((item) => item.count));

  // Estado CRM interactivo: ficha abierta, guardado/borrado, pestañas y formularios rápidos.
  const [editor, setEditor] = useState<Company | null>(null);
  const [tab, setTab] = useState<'DATOS' | 'CONTACTOS' | 'ACTIVIDAD'>('DATOS');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newContact, setNewContact] = useState({ name: '', email: '', role: '' });
  const [newInteraction, setNewInteraction] = useState<{ type: Interaction['type']; notes: string; nextSteps: string }>({ type: 'CALL', notes: '', nextSteps: '' });

  const openCompany = (c: Company) => {
    setError(null);
    setConfirmDelete(false);
    setTab('DATOS');
    setNewContact({ name: '', email: '', role: '' });
    setNewInteraction({ type: 'CALL', notes: '', nextSteps: '' });
    setEditor(c);
  };

  const editorContacts = useMemo(
    () => (editor ? contacts.filter((c) => c.companyId === editor.id) : []),
    [contacts, editor]
  );
  const editorInteractions = useMemo(
    () =>
      (editor ? interactions.filter((i) => i.companyId === editor.id) : []).sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      ),
    [interactions, editor]
  );

  const handleSave = async () => {
    if (!editor) return;
    if (!editor.name.trim()) {
      setError('Pon un nombre a la empresa.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await persistCompany(supabase, workspaceId, editor);
      setEditor(null);
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar.');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!editor) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteCompanyRow(supabase, editor.id);
      setEditor(null);
      setConfirmDelete(false);
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar.');
    }
    setDeleting(false);
  };

  const stepStage = async (company: Company, dir: -1 | 1) => {
    const idx = STAGE_ORDER.indexOf(company.stage);
    const next = STAGE_ORDER[idx + dir];
    if (!next) return;
    setBusyId(company.id);
    setError(null);
    try {
      await persistCompany(supabase, workspaceId, { ...company, stage: next });
      if (editor?.id === company.id) setEditor({ ...editor, stage: next });
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar de fase.');
    }
    setBusyId(null);
  };

  const handleAddContact = async () => {
    if (!editor || !supabase) return;
    if (!newContact.name.trim()) {
      setError('El contacto necesita al menos un nombre.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error: insError } = await supabase.from('contacts').insert({
        id: crypto.randomUUID(),
        company_id: editor.id,
        name: newContact.name.trim(),
        email: newContact.email.trim() || null,
        role: newContact.role.trim() || null,
        is_primary: editorContacts.length === 0,
      });
      if (insError) throw new Error(insError.message);
      setNewContact({ name: '', email: '', role: '' });
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo añadir el contacto.');
    }
    setSaving(false);
  };

  const handleDeleteContact = async (id: string) => {
    if (!supabase) return;
    setSaving(true);
    try {
      const { error: delError } = await supabase.from('contacts').delete().eq('id', id);
      if (delError) throw new Error(delError.message);
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar el contacto.');
    }
    setSaving(false);
  };

  const handleAddInteraction = async () => {
    if (!editor || !supabase) return;
    if (!newInteraction.notes.trim()) {
      setError('Escribe una nota para registrar la actividad.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const { error: insError } = await supabase.from('interactions').insert({
        id: crypto.randomUUID(),
        company_id: editor.id,
        date: new Date().toISOString(),
        type: newInteraction.type,
        notes: newInteraction.notes.trim(),
        next_steps: newInteraction.nextSteps.trim() || null,
      });
      if (insError) throw new Error(insError.message);
      setNewInteraction({ type: 'CALL', notes: '', nextSteps: '' });
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar.');
    }
    setSaving(false);
  };

  const handleDeleteInteraction = async (id: string) => {
    if (!supabase) return;
    try {
      const { error: delError } = await supabase.from('interactions').delete().eq('id', id);
      if (delError) throw new Error(delError.message);
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar.');
    }
  };

  return (
    <section className="w-full min-w-0 space-y-5 sm:space-y-7">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <MetricCard label="Total Leads" value={String(total)} hint="workspace activo" icon={Users} />
        <MetricCard label="Deals Vivos" value={String(liveDeals.length)} hint="MEETING / PROPOSAL" icon={Flame} />
        <MetricCard
          label="Score Medio"
          value={compactNumber.format(averageScore)}
          hint="sobre 10"
          icon={Target}
          className="col-span-2 sm:col-span-1"
        />
      </div>

      {/* Funnel visual: en móvil en 2 columnas compactas para no ocupar 1000px */}
      <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.02] p-4 sm:p-6">
        <div className="mb-3">
          <h2 className="text-lg sm:text-xl font-bold tracking-tight text-[#f5f2ec]">Funnel de Conversión</h2>
          <p className="text-xs sm:text-sm text-[#f5f2ec]/50">Prospectos por fase comercial.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
          {funnel.map((item) => (
            <div key={item.stage} className="flex min-h-[95px] sm:min-h-[130px] flex-col justify-between rounded-xl sm:rounded-2xl border border-white/10 bg-black/20 p-2.5 sm:p-3.5">
              <div className="flex items-center justify-between">
                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-[#f5f2ec]/60 truncate mr-1">
                  {item.label}
                </span>
                <span className="text-base sm:text-xl font-bold text-[#FF9800]">{item.count}</span>
              </div>
              <div className="mt-2 h-10 sm:h-14 w-full rounded-lg bg-white/[0.03] p-1 flex items-end">
                <div
                  className="w-full rounded-md bg-[#FF9800] transition-all"
                  style={{ height: `${Math.max(12, (item.count / maxStageCount) * 100)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Deals vivos prioritarios — tocan y abren ficha */}
      <div>
        <h2 className="mb-2.5 text-lg sm:text-xl font-bold tracking-tight text-[#f5f2ec]">Deals Vivos Prioritarios</h2>
        <p className="mb-2.5 text-xs text-[#f5f2ec]/50">Toca un deal para abrir su ficha y mover fase.</p>
        {liveDeals.length === 0 ? (
          <EmptyState
            title="No hay deals vivos actualmente"
            body="Cuando un lead pase a etapa de Reunión o Propuesta, se mostrará aquí con importe y días sin contacto."
          />
        ) : (
          <div className="grid min-w-0 gap-3 lg:grid-cols-2">
            {liveDeals.map(({ company, proposalTotal, days }) => (
              <button
                key={company.id}
                type="button"
                onClick={() => openCompany(company)}
                className="min-w-0 rounded-2xl sm:rounded-3xl border border-[#FF9800]/30 bg-[#FF9800]/[0.06] p-4 text-left shadow-sm transition active:scale-[0.99] hover:border-[#FF9800]/60 sm:p-6"
              >
                <div className="mb-4 sm:mb-6 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <span className="rounded-full border border-[#FF9800]/40 bg-[#FF9800]/15 px-2.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#FF9800]">
                      {STAGE_LABELS[company.stage] ?? company.stage}
                    </span>
                    <h3 className="mt-2 break-words text-xl sm:text-2xl font-bold leading-tight text-[#f5f2ec]">{company.name}</h3>
                    <p className="mt-0.5 text-xs sm:text-sm text-[#f5f2ec]/60">{company.sector}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-2xl sm:text-3xl font-extrabold text-[#FF9800]">
                      {proposalTotal ? currency.format(proposalTotal) : 'Sin €'}
                    </p>
                    <p className="text-[10px] sm:text-xs text-[#f5f2ec]/50">propuesta · toca para abrir</p>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-1.5 sm:gap-2">
                  <MiniStat label="Score" value={`${company.score}/10`} />
                  <MiniStat label="Último mov." value={days == null ? 'Sin dato' : `${days}d`} />
                  <MiniStat label="Canal" value={company.canal ? CANAL_LABELS[company.canal] : 'Sin canal'} />
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Listado filtrable de Leads — todo abre ficha */}
      <div>
        <div className="mb-3 flex flex-col gap-2.5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex min-w-0 items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-bold tracking-tight text-[#f5f2ec]">Leads Prospección</h2>
              <p className="text-xs sm:text-sm text-[#f5f2ec]/50">Toca un lead para editar, mover fase o añadir actividad.</p>
            </div>
            <button
              type="button"
              onClick={() => openCompany(newCompanyDraft(workspaceId))}
              className="inline-flex min-h-[48px] shrink-0 items-center gap-2 rounded-full bg-[#FF9800] px-5 text-sm font-bold text-black shadow-md transition active:scale-95 lg:hidden"
            >
              <Plus className="h-5 w-5" />
              Nuevo
            </button>
          </div>
          <div className="flex w-full min-w-0 flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <FilterStrip
              value={canalFilter}
              options={[
                ['ALL', 'Todos'],
                ['COLD_EMAIL', 'Email'],
                ['LINKEDIN', 'LinkedIn'],
                ['PHONE', 'Teléfono'],
                ['UNKNOWN', 'Sin canal'],
              ]}
              onChange={(value) => setCanalFilter(value as 'ALL' | LeadCanal | 'UNKNOWN')}
            />
            <FilterStrip
              value={sectorFilter}
              options={sectors.map((sector) => [sector, sector === 'ALL' ? 'Sectores' : sector])}
              onChange={setSectorFilter}
            />
            <button
              type="button"
              onClick={() => openCompany(newCompanyDraft(workspaceId))}
              className="hidden min-h-[44px] items-center gap-2 rounded-full bg-[#FF9800] px-5 text-sm font-bold text-black shadow-md transition active:scale-95 lg:inline-flex"
            >
              <Plus className="h-4 w-4" />
              Nuevo lead
            </button>
          </div>
        </div>

        {error && !editor && (
          <div className="mb-3 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300">{error}</div>
        )}

        {companies.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-white/15 bg-white/[0.015] px-4 py-8 text-center">
            <p className="text-base font-bold text-[#f5f2ec]">Pipeline vacío</p>
            <p className="mx-auto mt-1.5 max-w-md text-xs leading-relaxed text-[#f5f2ec]/50">No hay leads con esos filtros. Crea el primero.</p>
            <button
              type="button"
              onClick={() => openCompany(newCompanyDraft(workspaceId))}
              className="mx-auto mt-4 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-[#FF9800] px-6 text-sm font-bold text-black active:scale-95"
            >
              <Plus className="h-5 w-5" /> Crear lead
            </button>
          </div>
        ) : (
          <div className="grid min-w-0 gap-2.5 sm:gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {companies.map((company) => (
              <LeadCard key={company.id} company={company} busy={busyId === company.id} onOpen={() => openCompany(company)} onStep={(dir) => void stepStage(company, dir)} />
            ))}
          </div>
        )}
      </div>

      {editor && (
        <CompanyEditorModal
          draft={editor}
          setDraft={setEditor}
          contacts={editorContacts}
          interactions={editorInteractions}
          isNew={!companies.some((c) => c.id === editor.id) && !liveDeals.some((d) => d.company.id === editor.id)}
          saving={saving}
          deleting={deleting}
          confirmDelete={confirmDelete}
          setConfirmDelete={setConfirmDelete}
          error={error}
          tab={tab}
          setTab={setTab}
          newContact={newContact}
          setNewContact={setNewContact}
          newInteraction={newInteraction}
          setNewInteraction={setNewInteraction}
          onClose={() => {
            setEditor(null);
            setConfirmDelete(false);
          }}
          onSave={() => void handleSave()}
          onDelete={() => void handleDelete()}
          onAddContact={() => void handleAddContact()}
          onDeleteContact={(id) => void handleDeleteContact(id)}
          onAddInteraction={() => void handleAddInteraction()}
          onDeleteInteraction={(id) => void handleDeleteInteraction(id)}
        />
      )}
    </section>
  );
};

const FilterStrip: React.FC<{ value: string; options: string[][]; onChange: (value: string) => void }> = ({
  value,
  options,
  onChange,
}) => (
  <div className="flex w-full min-w-0 gap-1.5 overflow-x-auto no-scrollbar rounded-2xl border border-white/10 bg-white/[0.025] p-1.5 sm:w-auto">
    {options.map(([optionValue, label]) => (
      <button
        key={optionValue}
        type="button"
        onClick={() => onChange(optionValue)}
        className={`min-h-[44px] shrink-0 rounded-full px-4 text-xs font-semibold transition active:scale-95 ${
          value === optionValue
            ? 'bg-[#f5f2ec] text-black shadow-sm'
            : 'text-[#f5f2ec]/55 hover:bg-white/[0.06] hover:text-[#f5f2ec]'
        }`}
      >
        {label}
      </button>
    ))}
  </div>
);

const LeadCard: React.FC<{ company: Company; busy: boolean; onOpen: () => void; onStep: (dir: -1 | 1) => void }> = ({ company, busy, onOpen, onStep }) => (
  <article className="min-w-0 rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.025] p-4 shadow-sm transition hover:border-[#FF9800]/40 sm:p-5">
    <button type="button" onClick={onOpen} className="mb-3 block w-full text-left">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <span className="rounded-full border border-[#FF9800]/30 bg-[#FF9800]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#FF9800]">
            {STAGE_LABELS[company.stage] ?? company.stage}
          </span>
          <h3 className="mt-1.5 break-words text-lg sm:text-xl font-bold leading-tight">{company.name}</h3>
          <p className="break-words text-xs text-[#f5f2ec]/50">{company.sector}</p>
        </div>
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border border-[#FF9800]/30 bg-[#FF9800]/10 text-lg font-bold text-[#FF9800]">
          {company.score}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1 text-[11px] text-[#f5f2ec]/60">
        <span className="rounded-full border border-white/10 bg-black/20 px-2 py-0.5">
          {company.canal ? CANAL_LABELS[company.canal] : 'Sin canal'}
        </span>
        {company.region && (
          <span className="rounded-full border border-white/10 bg-black/20 px-2 py-0.5">{company.region}</span>
        )}
        {company.employeeCount && (
          <span className="rounded-full border border-white/10 bg-black/20 px-2 py-0.5">
            {company.employeeCount} emp.
          </span>
        )}
      </div>
      {company.notes && <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-[#f5f2ec]/50">{company.notes}</p>}
      <p className="mt-2 text-[11px] text-[#f5f2ec]/35">Toca para abrir ficha →</p>
    </button>
    <div className="flex items-center justify-between gap-2 border-t border-white/5 pt-2.5">
      <button
        type="button"
        onClick={() => onStep(-1)}
        disabled={busy}
        aria-label="Fase anterior"
        className="grid h-11 w-11 place-items-center rounded-full border border-white/10 text-[#f5f2ec]/70 active:scale-95 disabled:opacity-40"
      >
        ‹
      </button>
      <span className="text-[11px] text-[#f5f2ec]/40">{busy ? 'Guardando…' : 'Mover fase'}</span>
      <button
        type="button"
        onClick={() => onStep(1)}
        disabled={busy}
        aria-label="Fase siguiente"
        className="grid h-11 w-11 place-items-center rounded-full border border-[#FF9800]/40 bg-[#FF9800]/10 text-lg font-bold text-[#FF9800] active:scale-95 disabled:opacity-40"
      >
        ›
      </button>
    </div>
  </article>
);

/* ========================================================================= */
/* VISTA CLIENTES GANADOS — interactiva (abre la misma ficha CRM)          */
/* ========================================================================= */
const ClientsView: React.FC<{
  clients: Company[];
  projects: Project[];
  interactions: Interaction[];
  contacts: Contact[];
  workspaceId: string;
  onRefresh: () => Promise<void>;
}> = ({ clients, projects, interactions, contacts, workspaceId, onRefresh }) => {
  const [editor, setEditor] = useState<Company | null>(null);
  const [tab, setTab] = useState<'DATOS' | 'CONTACTOS' | 'ACTIVIDAD'>('DATOS');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newContact, setNewContact] = useState({ name: '', email: '', role: '' });
  const [newInteraction, setNewInteraction] = useState<{ type: Interaction['type']; notes: string; nextSteps: string }>({ type: 'CALL', notes: '', nextSteps: '' });

  const openClient = (c: Company) => {
    setError(null);
    setConfirmDelete(false);
    setTab('DATOS');
    setNewContact({ name: '', email: '', role: '' });
    setNewInteraction({ type: 'CALL', notes: '', nextSteps: '' });
    setEditor(c);
  };

  const editorContacts = useMemo(() => (editor ? contacts.filter((c) => c.companyId === editor.id) : []), [contacts, editor]);
  const editorInteractions = useMemo(
    () => (editor ? interactions.filter((i) => i.companyId === editor.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : []),
    [interactions, editor]
  );

  const handleSave = async () => {
    if (!editor) return;
    setSaving(true);
    setError(null);
    try {
      await persistCompany(supabase, workspaceId, editor);
      setEditor(null);
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar.');
    }
    setSaving(false);
  };
  const handleDelete = async () => {
    if (!editor) return;
    setDeleting(true);
    try {
      await deleteCompanyRow(supabase, editor.id);
      setEditor(null);
      setConfirmDelete(false);
      await onRefresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo borrar.');
    }
    setDeleting(false);
  };

  if (clients.length === 0) {
    return (
      <EmptyState
        title="Todavía no hay clientes cerrados"
        body="Los clientes con etapa CLOSED_WON aparecerán aquí junto con sus contactos, proyectos e interacciones."
      />
    );
  }

  return (
    <section className="w-full min-w-0 space-y-4">
      <div>
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#f5f2ec]">Clientes Ganados</h2>
        <p className="text-xs sm:text-sm text-[#f5f2ec]/50">Toca un cliente para editar ficha, contactos y actividad.</p>
      </div>

      {error && !editor && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-xs text-red-300">{error}</div>
      )}

      <div className="grid min-w-0 gap-3 sm:gap-4 lg:grid-cols-2">
        {clients.map((client) => {
          const clientProjects = projects.filter((project) => project.clientCompanyId === client.id);
          const clientInteractions = interactions.filter((interaction) => interaction.companyId === client.id).slice(0, 3);
          const primaryContact =
            contacts.find((contact) => contact.companyId === client.id && contact.isPrimary) ??
            contacts.find((contact) => contact.companyId === client.id);

          return (
            <button
              key={client.id}
              type="button"
              onClick={() => openClient(client)}
              className="min-w-0 rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.025] p-4 text-left shadow-sm transition active:scale-[0.99] hover:border-emerald-500/40 sm:p-6"
            >
              <div className="mb-4 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <span className="rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-emerald-400">
                    Cliente Activo
                  </span>
                  <h3 className="mt-1.5 break-words text-xl sm:text-2xl font-bold leading-tight">{client.name}</h3>
                  <p className="break-words text-xs sm:text-sm text-[#f5f2ec]/50">{client.sector}</p>
                </div>
                <CheckCircle2 className="h-6 w-6 text-emerald-400 shrink-0" />
              </div>

              {primaryContact && (
                <div className="mb-4 rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 p-3 text-xs sm:text-sm text-[#f5f2ec]/70">
                  <span className="font-semibold text-[#f5f2ec]">{primaryContact.name}</span>
                  {primaryContact.role ? ` · ${primaryContact.role}` : ''}
                  {primaryContact.email ? ` · ${primaryContact.email}` : ''}
                </div>
              )}

              <div className="space-y-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Proyectos asociados</p>
                {clientProjects.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-white/10 p-3 text-xs text-[#f5f2ec]/40">
                    Sin proyectos asociados todavía.
                  </p>
                ) : (
                  clientProjects.map((project) => <ProjectProgressBar key={project.id} project={project} />)
                )}
              </div>

              <div className="mt-4 space-y-1.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">Últimas interacciones</p>
                {clientInteractions.length === 0 ? (
                  <p className="text-xs text-[#f5f2ec]/40">Sin interacciones registradas. Toca para añadir.</p>
                ) : (
                  clientInteractions.map((interaction) => (
                    <div key={interaction.id} className="rounded-xl border-l-2 border-[#FF9800] bg-white/[0.015] pl-3 py-1.5 text-xs sm:text-sm">
                      <p className="text-[#f5f2ec]/80 line-clamp-2">{interaction.notes || interaction.type}</p>
                      <p className="mt-0.5 text-[10px] text-[#f5f2ec]/40">{formatDate(interaction.date)}</p>
                    </div>
                  ))
                )}
              </div>
              <p className="mt-3 text-[11px] text-[#f5f2ec]/35">Toca para abrir ficha →</p>
            </button>
          );
        })}
      </div>

      {editor && (
        <CompanyEditorModal
          draft={editor}
          setDraft={setEditor}
          contacts={editorContacts}
          interactions={editorInteractions}
          isNew={false}
          saving={saving}
          deleting={deleting}
          confirmDelete={confirmDelete}
          setConfirmDelete={setConfirmDelete}
          error={error}
          tab={tab}
          setTab={setTab}
          newContact={newContact}
          setNewContact={setNewContact}
          newInteraction={newInteraction}
          setNewInteraction={setNewInteraction}
          onClose={() => {
            setEditor(null);
            setConfirmDelete(false);
          }}
          onSave={() => void handleSave()}
          onDelete={() => void handleDelete()}
          onAddContact={() => {
            if (!editor || !supabase || !newContact.name.trim()) return;
            setSaving(true);
            void supabase
              .from('contacts')
              .insert({ id: crypto.randomUUID(), company_id: editor.id, name: newContact.name.trim(), email: newContact.email.trim() || null, role: newContact.role.trim() || null, is_primary: editorContacts.length === 0 })
              .then(async ({ error: e }) => {
                if (e) setError(e.message);
                else {
                  setNewContact({ name: '', email: '', role: '' });
                  await onRefresh();
                }
                setSaving(false);
              });
          }}
          onDeleteContact={(id) => {
            if (!supabase) return;
            void supabase
              .from('contacts')
              .delete()
              .eq('id', id)
              .then(async ({ error: e }) => {
                if (e) setError(e.message);
                else await onRefresh();
              });
          }}
          onAddInteraction={() => {
            if (!editor || !supabase || !newInteraction.notes.trim()) return;
            setSaving(true);
            void supabase
              .from('interactions')
              .insert({ id: crypto.randomUUID(), company_id: editor.id, date: new Date().toISOString(), type: newInteraction.type, notes: newInteraction.notes.trim(), next_steps: newInteraction.nextSteps.trim() || null })
              .then(async ({ error: e }) => {
                if (e) setError(e.message);
                else {
                  setNewInteraction({ type: 'CALL', notes: '', nextSteps: '' });
                  await onRefresh();
                }
                setSaving(false);
              });
          }}
          onDeleteInteraction={(id) => {
            if (!supabase) return;
            void supabase
              .from('interactions')
              .delete()
              .eq('id', id)
              .then(async ({ error: e }) => {
                if (e) setError(e.message);
                else await onRefresh();
              });
          }}
        />
      )}
    </section>
  );
};

const ProjectProgressBar: React.FC<{ project: Project }> = ({ project }) => {
  const progress = Math.max(0, Math.min(100, project.progress ?? 0));
  return (
    <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 p-3">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <p className="text-xs sm:text-sm font-bold text-[#f5f2ec] truncate">{project.name}</p>
        <span className="text-xs font-bold text-[#FF9800]">{progress}%</span>
      </div>
      <div className="h-1.5 sm:h-2 w-full rounded-full bg-white/[0.08] overflow-hidden">
        <div className="h-full rounded-full bg-[#FF9800]" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

/* ========================================================================= */
/* VISTA MÉTRICAS SEMANALES (DENTRO DE GESTIÓN)                             */
/* ========================================================================= */
const WeeklyMetricsView: React.FC<{ metrics: WeeklyMetric[]; chartData: any[] }> = ({ metrics, chartData }) => {
  if (metrics.length === 0) {
    return (
      <EmptyState
        title="Sin métricas semanales"
        body="Cuando se registren datos en weekly_metrics, aquí aparecerán tracción comercial, ritmo y hábitos semanales."
      />
    );
  }

  const latest = metrics[metrics.length - 1];

  return (
    <section className="space-y-4 sm:space-y-6">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard label="Contactadas" value={String(latest.empresasContactadas)} hint="última semana" icon={Mail} />
        <MetricCard label="Conversaciones" value={String(latest.conversaciones)} hint="última semana" icon={MessageSquare} />
        <MetricCard label="Horas móvil" value={`${compactNumber.format(latest.movilHoras)}h`} hint="meta: 2h" icon={Phone} />
        <MetricCard label="Autovaloración" value={String(latest.autovaloracion)} hint="meta: 8" icon={TrendingUp} />
      </div>

      <ChartPanel title="Tracción comercial" subtitle="Empresas contactadas, conversaciones y posts semanales.">
        <ResponsiveContainer width="100%" height={230}>
          <ComposedChart data={chartData}>
            <CartesianGrid stroke="rgba(245,242,236,0.08)" vertical={false} />
            <XAxis dataKey="week" tick={{ fill: 'rgba(245,242,236,.55)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fill: 'rgba(245,242,236,.45)', fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="empresas" fill="#FF9800" radius={[6, 6, 0, 0]} />
            <Line type="monotone" dataKey="conversaciones" stroke="#f5f2ec" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="posts" stroke="rgba(245,242,236,.45)" strokeWidth={2} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </ChartPanel>

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
        <ChartPanel title="Entreno y móvil" subtitle="Línea naranja discontinua: objetivo 4 -> 3 -> 2h.">
          <ResponsiveContainer width="100%" height={210}>
            <ComposedChart data={chartData}>
              <CartesianGrid stroke="rgba(245,242,236,0.08)" vertical={false} />
              <XAxis dataKey="week" tick={{ fill: 'rgba(245,242,236,.55)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(245,242,236,.45)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <Area type="monotone" dataKey="entreno" fill="rgba(255,152,0,.22)" stroke="#FF9800" strokeWidth={2} />
              <Line type="monotone" dataKey="movil" stroke="#f5f2ec" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="movilObjetivo" stroke="#FF9800" strokeDasharray="6 6" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel title="Calidad y venta" subtitle="Autovaloración con objetivo 8, diagnósticos y retainers.">
          <ResponsiveContainer width="100%" height={210}>
            <AreaChart data={chartData}>
              <CartesianGrid stroke="rgba(245,242,236,0.08)" vertical={false} />
              <XAxis dataKey="week" tick={{ fill: 'rgba(245,242,236,.55)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 10]} tick={{ fill: 'rgba(245,242,236,.45)', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ChartTooltip />} />
              <ReferenceLine y={8} stroke="#FF9800" strokeDasharray="6 6" />
              <Area type="monotone" dataKey="autovaloracion" fill="rgba(255,152,0,.2)" stroke="#FF9800" strokeWidth={2} />
              <Line type="monotone" dataKey="diagnosticos" stroke="#f5f2ec" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="retainers" stroke="rgba(245,242,236,.45)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartPanel>
      </div>
    </section>
  );
};

const ChartPanel: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({
  title,
  subtitle,
  children,
}) => (
  <div className="rounded-2xl sm:rounded-3xl border border-white/10 bg-white/[0.025] p-3.5 sm:p-6 shadow-sm">
    <div className="mb-3 sm:mb-5">
      <h3 className="text-lg sm:text-xl font-bold tracking-tight">{title}</h3>
      <p className="mt-0.5 text-xs sm:text-sm text-[#f5f2ec]/50">{subtitle}</p>
    </div>
    {children}
  </div>
);

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-white/15 bg-[#141414] p-2.5 sm:p-3.5 shadow-2xl">
      <p className="mb-1 text-[11px] sm:text-xs font-bold uppercase tracking-wider text-[#FF9800]">{label}</p>
      <div className="space-y-1">
        {payload.map((item: any) => (
          <p key={item.dataKey} className="text-xs sm:text-sm text-[#f5f2ec]/75">
            {item.name}: <span className="font-bold text-[#f5f2ec]">{item.value}</span>
          </p>
        ))}
      </div>
    </div>
  );
};

/* ========================================================================= */
/* VISTA CAJA (DENTRO DE GESTIÓN)                                           */
/* ========================================================================= */
const CashView: React.FC<{
  ledger: CashLedgerEntry[];
  companyById: Map<string, Company>;
  summary: { totalCobrado: number; facturado: number; previsto: number; cobrado: number; progress: number; remaining: number };
}> = ({ ledger, companyById, summary }) => (
  <section className="space-y-5 sm:space-y-7">
    {/* Termómetro de Caja estilo Google */}
    <div className="rounded-2xl sm:rounded-3xl border border-[#FF9800]/30 bg-[#FF9800]/[0.08] p-4 sm:p-8 shadow-sm">
      <div className="mb-5 sm:mb-8 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <span className="rounded-full border border-[#FF9800]/40 bg-[#FF9800]/15 px-2.5 py-0.5 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-[#FF9800]">
            Norte de la empresa
          </span>
          <h2 className="mt-2 text-2xl sm:text-6xl font-extrabold leading-none tracking-tight">
            Caja -&gt; 3.000€
          </h2>
          <p className="mt-2 max-w-2xl text-xs sm:text-sm leading-relaxed text-[#f5f2ec]/70">
            Al alcanzar 3.000€ cobrados reales en cuenta, SDAI tiene luz verde para constituir la S.L.
          </p>
        </div>
        <div className="text-left sm:text-right">
          <p className="text-3xl sm:text-7xl font-extrabold text-[#FF9800]">{currency.format(summary.totalCobrado)}</p>
          <p className="text-[10px] sm:text-xs font-medium text-[#f5f2ec]/50">cobrado real en banco</p>
        </div>
      </div>

      {/* Barra de progreso redondeada en píldora */}
      <div className="h-5 sm:h-6 w-full rounded-full border border-[#FF9800]/35 bg-black/40 p-1">
        <div
          className="h-full rounded-full bg-[#FF9800] transition-all duration-700 shadow-sm"
          style={{ width: `${summary.progress}%` }}
        />
      </div>
      <div className="mt-2.5 flex items-center justify-between text-[11px] sm:text-xs font-semibold text-[#f5f2ec]/60">
        <span>{compactNumber.format(summary.progress)}% completado</span>
        <span>Faltan {currency.format(summary.remaining)}</span>
      </div>
    </div>

    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      <MetricCard label="Facturado total" value={currency.format(summary.facturado)} icon={CircleDollarSign} />
      <MetricCard label="Cobrado real" value={currency.format(summary.cobrado)} icon={CheckCircle2} />
      <MetricCard
        label="Previsto"
        value={currency.format(summary.previsto)}
        icon={CalendarClock}
        className="col-span-2 sm:col-span-1"
      />
    </div>

    <div>
      <h3 className="mb-2.5 text-lg sm:text-xl font-bold tracking-tight text-[#f5f2ec]">Movimientos de Caja</h3>
      {ledger.length === 0 ? (
        <EmptyState
          title="Caja sin movimientos"
          body="Los cobros y facturas registradas en cash_ledger aparecerán aquí agrupados visualmente."
        />
      ) : (
        <div className="space-y-2">
          {ledger.map((entry, index) => {
            const company = entry.companyId ? companyById.get(entry.companyId) : undefined;
            return (
              <article
                key={entry.id ?? `${entry.date}-${index}`}
                className="flex items-center justify-between gap-3 rounded-xl sm:rounded-2xl border border-white/10 bg-white/[0.025] p-3 sm:p-4 shadow-sm transition hover:border-white/20"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <p className="truncate text-sm sm:text-base font-bold text-[#f5f2ec]">{entry.concepto}</p>
                    <span
                      className={`rounded-full px-2 py-0.2 text-[10px] font-bold ${
                        entry.cobrado
                          ? 'bg-[#FF9800] text-black shadow-sm'
                          : 'border border-white/10 bg-white/[0.03] text-[#f5f2ec]/60'
                      }`}
                    >
                      {entry.tipo}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] sm:text-xs text-[#f5f2ec]/50">
                    {formatDate(entry.date)}
                    {company ? ` · ${company.name}` : ''}
                  </p>
                  {entry.notas && (
                    <p className="mt-1 line-clamp-1 text-[11px] text-[#f5f2ec]/50">{entry.notas}</p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-lg sm:text-2xl font-bold text-[#f5f2ec]">{currency.format(entry.importe)}</p>
                  <ArrowUpRight className="ml-auto mt-0.5 h-3.5 w-3.5 text-[#FF9800]" />
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  </section>
);

const MiniStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/30 p-2 sm:p-3">
    <p className="text-[10px] font-semibold uppercase tracking-wider text-[#f5f2ec]/50">{label}</p>
    <p className="mt-0.5 truncate text-xs sm:text-sm font-bold text-[#f5f2ec]">{value}</p>
  </div>
);
